import { ILogger } from "@data-types/logger/ILogger";
import * as Sentry from "@sentry/browser";
import { makeFetchTransport } from "@sentry/react";
import { captureException } from "@sentry/react";
import { UserAuthStore } from "@state/shared";
import { getDsn } from "@utils/sentry/getDsn";
import { getIntegrations } from "@utils/sentry/getIntegrations";
import { getReleaseVersion } from "@utils/sentry/getReleaseVersion";

export class SentryLogger implements ILogger {
	isInitialized: boolean = false;
	isEnabled: boolean;
	constructor() {
		this.isEnabled =
			WEBPACK_IS_SENTRY_ENABLED && !WEBPACK_IS_SENTRY_DEV_BUILD;
		this.onInitialize();
	}

	private onInitialize() {
		if (this.isInitialized) return;
		if (!this.isEnabled) {
			return;
		}

		const dsn = getDsn();
		const release = getReleaseVersion();

		Sentry.init({
			release: release,
			dsn: dsn,
			integrations: getIntegrations(),
			ignoreErrors: [
				"Unhandled Promise Rejection:",
				"ResizeObserver",
				"Connection started reconnecting before invocation result was received",
				"Loading chunk",
				"Failed to fetch",
				"Error during negotiation request.",
				"The connection was stopped during page load.",
				"Unauthorized",
				"The connection was stopped during the negotiate request.",
				"No transport could be initialized successfully. Try specifying a different transport or none at all for auto initialization.",
				"Object captured as promise rejection",
				"AxiosError: Network Error",
				"NetworkError when attempting to fetch",
				"Non-Error promise rejection captured with value: Timeout",
				"Error during start request. Stopping the connection",
				"redeclaration of const hideMyLocation",
				"The connection was stopped during the start request",
				"Load failed",
				"Widget embedding aborted",
				"Widget could not be loaded",
				"Invalid widget element",
				"STATSCOREWidgets",
				"Invalid token",
				"Can not find betting type",
				"Amity SDK",
				"Cannot read properties of null (reading 'clearRect')",
				"The Web Socket transport is in an invalid state, transitioning into reconnecting",
				"Failed to read the 'localStorage' property from 'Window': Access is denied for this document",
				"Connection was disconnected before invocation result was received",
				"No error message",
				"(evaluating 't.clearRect')",
			],
			tracesSampleRate: WEBPACK_SENTRY_TRACES_SAMPLE_RATE,
			sampleRate: WEBPACK_SENTRY_SAMPLE_RATE,
			replaysSessionSampleRate:
				WEBPACK_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
			replaysOnErrorSampleRate:
				WEBPACK_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
			transport: (options) => {
				const newUrl = options.url.replace("sentry_key=", "key=");
				const newerUrl = newUrl.replace("sentry_version=", "version=");

				return makeFetchTransport({ ...options, url: newerUrl });
			},
			beforeSend(event) {
				const errorValue = event?.exception?.values?.[0]?.value || "";
				const requestUrl =
					(typeof event?.message === "object" &&
					event.message !== null
						? /* @ts-expect-error  */
							event.message.url
						: "") || "";

				// Ignore Error: [object Response] from widgets.statscore api
				if (
					errorValue === "[object Response]" &&
					requestUrl.includes("/render-widget")
				) {
					console.warn(
						"Ignored known widget error:",
						errorValue,
						requestUrl
					);
					return null;
				}

				console.log("Sentry event captured:", event);
				return event;
			},
		});
		this.isInitialized = true;
	}
	logError(
		error: unknown,
		hint: Parameters<typeof Sentry.captureException>[1] = {}
	): void {
		if (!this.isEnabled) {
			return;
		}

		captureException(error, hint);
	}

	setUserContext(store?: UserAuthStore | null) {
		if (!this.isEnabled) {
			return;
		}

		let userCtx = null;
		if (store) {
			userCtx = {
				beaconTag: store?.user?.id || "Anon",
				...(store.secondaryUser && {
					secondaryBeaconTag: store.secondaryUser?.id,
				}),
			};
		}
		Sentry.setContext("user", {
			user: userCtx || "Anon",
		});
	}
}
